import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Truck, CreditCard, MapPin, Receipt, ShieldCheck, Clock, Star } from "lucide-react";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import Tracking from "../components/Tracking";
import OrderRatingDialog from "../components/OrderRatingDialog";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";

const inr = (n) => `₹${Number(n || 0).toFixed(2)}`;

const STATUS_META = {
    created: { color: "bg-zinc-100 text-zinc-700", label: "Created" },
    payment_pending: { color: "bg-amber-50 text-amber-700 border border-amber-200", label: "Payment pending" },
    paid: { color: "bg-emerald-50 text-emerald-700 border border-emerald-200", label: "Paid" },
    payment_failed: { color: "bg-red-50 text-red-700 border border-red-200", label: "Failed" },
    cancelled: { color: "bg-zinc-100 text-zinc-500", label: "Cancelled" },
};

const Section = ({ title, icon, children }) => (
    <div className="rounded-3xl border border-zinc-100 bg-white p-6 shadow-soft">
        <p className="label-uppercase flex items-center gap-1.5">{icon}{title}</p>
        <div className="mt-3">{children}</div>
    </div>
);

const Detail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [cancelling, setCancelling] = useState(false);
    const [rateOpen, setRateOpen] = useState(false);

    const load = async () => {
        try {
            setLoading(true);
            const { data } = await api.get(`/orders/${id}`);
            setOrder(data);
        } catch {
            setOrder(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [id]); // eslint-disable-line

    // Poll while still pending so the UI updates after webhook
    useEffect(() => {
        if (!order || !["created", "payment_pending"].includes(order.status)) return;
        const t = setInterval(load, 5000);
        return () => clearInterval(t);
    }, [order]); // eslint-disable-line

    const onCancel = async () => {
        if (!order || cancelling) return;
        if (!window.confirm("Cancel this order?")) return;
        try {
            setCancelling(true);
            const { data } = await api.post(`/orders/${order.id}/cancel`);
            setOrder(data);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            alert(typeof detail === "string" ? detail : detail?.message || "Cancel failed");
        } finally {
            setCancelling(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-3">
                <div className="h-10 w-1/3 skeleton" />
                <div className="h-32 skeleton rounded-3xl" />
                <div className="h-48 skeleton rounded-3xl" />
            </div>
        );
    }

    if (!order) {
        return (
            <div className="text-center">
                <h2 className="text-xl font-semibold">Order not found</h2>
                <Link to="/orders" className="btn-brand mt-6 inline-flex">Back to orders</Link>
            </div>
        );
    }

    const meta = STATUS_META[order.status] || STATUS_META.created;
    const a = order.shipping_address;
    const sq = order.shipping_quote;
    const amt = order.amounts;
    const pay = order.payment;
    const cancellable = ["created", "payment_pending"].includes(order.status);

    return (
        <div data-testid="order-detail-page">
            <Link to="/orders" className="mb-6 inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-brand-700">
                <ArrowLeft className="h-4 w-4" /> All orders
            </Link>

            <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <p className="label-uppercase">Order</p>
                    <h1 className="mt-1 text-3xl font-semibold tracking-tight" data-testid="order-no">{order.order_no}</h1>
                    <p className="mt-1 text-sm text-zinc-500">Placed {new Date(order.created_at).toLocaleString()}</p>
                    {user?.pills_id && (
                        <p className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-mono text-zinc-600 dark:text-zinc-400" data-testid="order-pills-id">
                            PILLS Loyalty ID: <strong>{user.pills_id}</strong>
                        </p>
                    )}
                </div>
                <span className={`inline-flex rounded-full px-3 py-1 text-sm font-medium ${meta.color}`} data-testid="order-status-pill">
                    {meta.label}
                </span>
            </header>

            <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
                <div className="space-y-6">
                    <Section title="Items" icon={<Receipt className="h-3.5 w-3.5" />}>
                        <ul className="divide-y divide-zinc-100">
                            {order.items.map((it) => (
                                <li key={it.product_id} className="flex items-center gap-4 py-3" data-testid={`order-item-${it.product_id}`}>
                                    <img src={it.image} alt={it.name} className="h-14 w-14 rounded-xl object-cover ring-1 ring-zinc-200" />
                                    <div className="min-w-0 flex-1">
                                        <p className="line-clamp-1 text-sm font-medium">{it.name}</p>
                                        <p className="text-xs text-zinc-500">{it.brand} • {it.pack_size}</p>
                                        {it.prescription_id && (
                                            <Link to={`/prescriptions/${it.prescription_id}`} className="mt-1 inline-flex items-center gap-1 text-[11px] text-brand-700 hover:underline">
                                                <ShieldCheck className="h-3 w-3" /> Rx attached
                                            </Link>
                                        )}
                                    </div>
                                    <div className="text-right text-sm">
                                        <p>{inr(it.selling_price)} × {it.qty}</p>
                                        <p className="font-semibold">{inr(it.line_total)}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </Section>

                    <Section title="Delivery address" icon={<MapPin className="h-3.5 w-3.5" />}>
                        <p className="text-sm font-medium text-zinc-900">{a.label}</p>
                        <p className="mt-1 text-sm text-zinc-600">{a.line1}{a.line2 ? `, ${a.line2}` : ""}</p>
                        <p className="text-sm text-zinc-600">{a.city}, {a.state} - {a.pincode}</p>
                        <p className="text-sm text-zinc-500">{a.phone}</p>
                    </Section>

                    <Section title="Shipping" icon={<Truck className="h-3.5 w-3.5" />}>
                        <p className="text-sm font-medium text-zinc-900">{sq.courier_name}</p>
                        <p className="mt-1 text-xs text-zinc-500">
                            {sq.etd ? `ETA ${sq.etd}` : "ETA: TBD"} • {sq.weight_kg} kg • {sq.source === "shiprocket" ? "Live rate" : "Default rate"}
                        </p>
                    </Section>

                    {["packed", "dispatched", "delivered"].includes(order.status) && (
                        <Tracking orderId={order.id} />
                    )}

                    {order.status === "delivered" && (
                        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-zinc-100 bg-gradient-to-r from-amber-50/40 to-white p-5 dark:from-amber-950/20 dark:to-zinc-900" data-testid="order-return-cta">
                            <div>
                                <p className="text-sm font-medium text-zinc-900 dark:text-zinc-50">
                                    {order.rating ? `You rated this order ${order.rating}/5` : "Tell us how we did"}
                                </p>
                                <p className="text-xs text-zinc-500">
                                    {order.rating
                                        ? "Thanks for the feedback! You can still request a return within 14 days."
                                        : "Within 14 days of delivery you can request a refund — or rate the order."}
                                </p>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                {!order.rating && (
                                    <button
                                        type="button"
                                        onClick={() => setRateOpen(true)}
                                        className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-amber-600"
                                        data-testid="order-rate-btn"
                                    >
                                        <Star className="h-3.5 w-3.5" /> Rate this order
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => navigate(`/returns/new?order_id=${order.id}`)}
                                    className="rounded-full border border-amber-300 bg-white px-4 py-2 text-sm font-medium text-amber-800 hover:bg-amber-50"
                                    data-testid="order-return-btn"
                                >
                                    Request a return
                                </button>
                            </div>
                        </div>
                    )}
                    {rateOpen && (
                        <OrderRatingDialog
                            open={rateOpen}
                            onClose={() => setRateOpen(false)}
                            order={{ id: order.id, order_no: order.order_no }}
                            onRated={() => { load(); }}
                        />
                    )}

                    <Section title="Status timeline" icon={<Clock className="h-3.5 w-3.5" />}>
                        <ol className="space-y-3">
                            {(order.history || []).map((h, i) => (
                                <li key={i} className="flex items-start gap-3 text-sm" data-testid={`history-${i}`}>
                                    <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-brand-500" />
                                    <div>
                                        <p className="font-medium text-zinc-900">{h.event.replace(/_/g, " ")}</p>
                                        <p className="text-xs text-zinc-500">{new Date(h.at).toLocaleString()} • {h.actor}{h.note ? ` • ${h.note}` : ""}</p>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </Section>
                </div>

                <aside className="space-y-4 self-start">
                    <Section title="Payment" icon={<CreditCard className="h-3.5 w-3.5" />}>
                        <p className="text-sm font-medium text-zinc-900">Zoho Payments</p>
                        <p className="mt-1 text-xs text-zinc-500">Status: <span className="font-medium text-zinc-700">{pay.status}</span></p>
                        {pay.payment_id && <p className="text-xs text-zinc-500">Payment ID: {pay.payment_id}</p>}
                        {pay.method && <p className="text-xs text-zinc-500">Method: {pay.method}</p>}
                        {pay.redirect_url && pay.status === "pending" && (
                            <a href={pay.redirect_url} className="btn-brand mt-4 w-full">
                                Resume payment
                            </a>
                        )}
                    </Section>

                    <Section title="Summary" icon={<Receipt className="h-3.5 w-3.5" />}>
                        <dl className="space-y-2 text-sm">
                            <div className="flex justify-between"><dt className="text-zinc-500">Subtotal</dt><dd>{inr(amt.subtotal)}</dd></div>
                            <div className="flex justify-between"><dt className="text-zinc-500">Shipping</dt><dd>{inr(amt.shipping_fee)}</dd></div>
                            <div className="flex justify-between"><dt className="text-zinc-500">GST {amt.gst_rate_percent}%</dt><dd>{inr(amt.tax_gst)}</dd></div>
                            <div className="flex justify-between border-t border-zinc-100 pt-2"><dt className="font-medium">Total</dt><dd className="text-lg font-semibold">{inr(amt.total)}</dd></div>
                        </dl>
                    </Section>

                    {cancellable && (
                        <button onClick={onCancel} disabled={cancelling} className="w-full rounded-full border border-zinc-200 bg-white px-5 py-3 text-sm font-medium text-zinc-700 hover:border-red-200 hover:text-red-600 disabled:opacity-50" data-testid="order-cancel-btn">
                            {cancelling ? "Cancelling..." : "Cancel order"}
                        </button>
                    )}
                </aside>
            </div>
        </div>
    );
};

const OrderDetail = () => (
    <Layout>
        <section className="mx-auto max-w-6xl px-6 py-10 lg:px-12">
            <AuthGuard>
                <Detail />
            </AuthGuard>
        </section>
    </Layout>
);

export default OrderDetail;
